import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Loader2,
  CheckCircle,
  XCircle,
  CalendarClock,
  CalendarDays,
  X,
} from 'lucide-react';
import axios from 'axios';
import { useSuperAdmin } from '../context/SuperAdminContext';
import {normalizeShift,formatINR,formatDatePart,formatTimePart,toDateInputValue,STATUS_STYLES , STATUS_LABELS} from "../helpers/change_payout_date_helper"


//  component 
const Paymentwithdraw = ({ isOpen, onClose, workerId }) => {
  const {payout_change_date_shifts, change_payout_date}=useSuperAdmin();

  const [shifts, setShifts] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });
  const [reason, setReason] = useState("")
  const [modalStep, setModalStep] = useState(null);

  const tomorrow = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate());
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const tomorrowValue = useMemo(() => toDateInputValue(tomorrow), [tomorrow]);
  const [selectedDate, setSelectedDate] = useState(tomorrowValue);


  const showNotification = (type, message) => setNotification({ show: true, type, message });
  const closeNotification = () => setNotification({ show: false, type: '', message: '' });

  useEffect(() => {
    if (!notification.show) return;
    const timer = setTimeout(closeNotification, 4000);
    return () => clearTimeout(timer);
  }, [notification.show]);

  // Fetch shifts (optionally scoped to workerId) and preselect all of them
  const fetchShifts = useCallback(async () => {
    setLoading(true);

    try {
      const response = await payout_change_date_shifts(workerId);

      const rawList = response.data?.data?.shifts || [];
      const normalized = rawList.map(normalizeShift);

      setShifts(normalized);
      setSelectedIds(new Set(normalized.map((s) => s.id)));
    } catch (error) {
      console.error('Error fetching shifts:', error);
      setShifts([]);
    } finally {
      setLoading(false);
    }
  }, [workerId]);

useEffect(() => {
  if (!isOpen) return;
  fetchShifts();
}, [isOpen]);

  const allSelected = shifts.length > 0 && selectedIds.size === shifts.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(shifts.map((s) => s.id)));
  };

  const toggleShift = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };



  // Summary computed from currently SELECTED shifts only
  const summary = useMemo(() => {
    const selectedShifts = shifts.filter((s) => selectedIds.has(s.id));
    return selectedShifts.reduce(
      (acc, s) => ({
        totalShifts: acc.totalShifts + 1,
        totalPayment: acc.totalPayment + s.payment,
      }),
      { totalShifts: 0,  totalPayment: 0 }
    );
  }, [shifts, selectedIds]);

    if (!isOpen) return null;

  //  change payout date flow 

  const openConfirmModal = () => {
    if (selectedIds.size === 0) return;
    closeNotification();
    setModalStep('pickDate');
  };

  const goToDatePicker = () => {
    setSelectedDate(tomorrowValue);
    setModalStep('pickDate');
  };

  const closeModal = () => setModalStep(null);

  const handleConfirmDateChange = async () => {
    if (!selectedDate) return;
    setSubmitting(true);

    try {
      const response =await change_payout_date(workerId,Array.from(selectedIds),selectedDate,reason);

      if (response.data.success) {
        showNotification(
          'success',
          response.data.message ||
            `Payout date updated to ${formatDatePart(selectedDate)} for ${summary.totalShifts} shift(s).`
        );
        setModalStep(null);
        fetchShifts();
      } else {
        showNotification('error', response.data.message || 'Failed to update payout date.');
      }
    } catch (error) {
      console.error('Error changing payout date:', error);
      showNotification(
        'error',
        error.response?.data?.message || 'An error occurred while updating the payout date.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // render pieces 

  const renderNotification = () => {
    if (!notification.show) return null;
    const isSuccess = notification.type === 'success';
    return (
      <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[1200] w-full max-w-sm px-4">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="flex items-center px-4 py-3">
            {isSuccess ? (
              <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" fill="currentColor" strokeWidth={0} />
            ) : (
              <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" fill="currentColor" strokeWidth={0} />
            )}
            <p className="ml-3 flex-1 text-sm font-medium text-gray-700 ">
              {notification.message}
            </p>
            <button onClick={closeNotification} className="ml-3 flex-shrink-0">
              <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
            </button>
          </div>
          <div className="h-1 w-full bg-gray-100">
            <div
              key={notification.message}
              className={`h-full ${isSuccess ? 'bg-green-500' : 'bg-red-500'} animate-[toastShrink_4s_linear_forwards]`}
            />
          </div>
        </div>
        <style>{`
          @keyframes toastShrink {
            from { width: 100%; }
            to { width: 0%; }
          }
        `}</style>
      </div>
    );
  };

  const renderSummary = () => (
    <div className="bg-white rounded-lg py-2">
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
        <span className="flex items-center text-gray-700">
          <span className="h-2 w-2 rounded-full bg-pink-500 mr-2" />
          Total Shifts: <span className="font-medium ml-1">{summary.totalShifts}</span>
        </span>
        <span className="flex items-center text-gray-700">
          <span className="h-2 w-2 rounded-full bg-teal-500 mr-2" />
          Total Payment: <span className="font-medium ml-1">{formatINR(summary.totalPayment)}</span>
        </span>
      </div>
    </div>
  );

  const renderTable = () => (
    <div className="rounded-lg overflow-hidden mb-6">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="sticky top-0 z-10">
            <tr className="bg-[#F6FBFF] border-b border-gray-200">
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => el && (el.indeterminate = someSelected)}
                  onChange={toggleSelectAll}
                  className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
              </th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Entry Date &amp; Time</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Company</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Department</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Working Hours</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Payment</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Eligible Date for Payment</th>
              
            </tr>
          </thead>
          <tbody>
            {shifts.map((shift) => {
              const checked = selectedIds.has(shift.id);
              return (
                <tr
                  key={shift.id}
                  className={`border-b border-gray-100 last:border-b-0 cursor-pointer transition-colors ${
                    checked ? 'bg-white' : 'bg-white hover:bg-gray-50'
                  }`}
                  onClick={() => toggleShift(shift.id)}
                >
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleShift(shift.id)}
                      className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-gray-800">{formatDatePart(shift.entryDate)}</p>
                    <p className="text-xs text-gray-400">{formatTimePart(shift.entryDate)}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{shift.companyName}</td>
                  <td className="px-4 py-3 text-gray-700">{shift.departmentName}</td>
                  <td className="px-4 py-3 text-gray-700">{shift.workingHours.toFixed(2)} hrs</td>
                  <td className="px-4 py-3">
                    <span className="inline-block px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 font-medium">
                      {formatINR(shift.payment)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {shift.eligibleDate ? formatDatePart(shift.eligibleDate) : 'Not scheduled'}
                  </td>
                 
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );



const renderDateModal = () => {
    if (modalStep !== 'pickDate') return null;
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1100] p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center">
              <div className="h-10 w-10 rounded-full bg-blue-50 flex items-center justify-center mr-3 flex-shrink-0">
                <CalendarDays className="h-5 w-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">Select new payout date</h3>
            </div>
            
            <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>

          <p className="text-sm text-gray-500 mb-2 ml-2">
                change the payment date for{' '}
                <span className="font-medium text-gray-700">{summary.totalShifts} shift(s)</span> totaling{' '}
                <span className="font-medium text-gray-700">{formatINR(summary.totalPayment)}</span>.
              </p>


          <input
            type="date"
            value={selectedDate}
            min={tomorrowValue}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />

          <div className="mt-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason for change
            </label>
            <textarea
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Enter reason for changing the payout date..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            />
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={closeModal}
              disabled={submitting}
              className="px-4 py-2 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmDateChange}
              disabled={submitting || !selectedDate || !reason.trim()}
              className="px-4 py-2 rounded-md text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-50 flex items-center"
            >
              {submitting ? (
                <>
                  <Loader2 className="animate-spin h-4 w-4 mr-2" />
                  Updating...
                </>
              ) : (
                'Confirm'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // main render 
  const body = (
    <div className="flex flex-col h-full min-h-0">
      {/* Fixed section: title, notification, summary + action button */}
      <div className="flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xl font-bold text-gray-800">Change Payment Date</h2>
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 flex-shrink-0"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
          )}
        </div>

        {renderNotification()}

        {!loading && shifts.length > 0 && (
          <div className="flex items-center justify-between gap-4 mb-4 pb-4 border-b border-gray-100">
            {renderSummary()}
            <button
              onClick={openConfirmModal}
              disabled={selectedIds.size === 0}
              className="flex-shrink-0 bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              <CalendarClock className="h-4 w-4 mr-1.5" />
              Change Payment Date
            </button>
          </div>
        )}
      </div>

      {/* Scrollable section: shifts table */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <Loader2 className="h-8 w-8 animate-spin mb-2" />
            <p className="text-sm">Loading shifts...</p>
          </div>
        ) : shifts.length === 0 ? (
          <div className="text-center py-16 text-gray-400 text-sm">No shifts available right now.</div>
        ) : (
          renderTable()
        )}
      </div>

      {renderDateModal()}
    </div>
  );

  if (onClose) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden p-6">
          {body}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-y-auto pt-16 bg-gray-100 p-4">
      <div className="max-w-4xl mx-auto">
        <div
          className="bg-white shadow-lg rounded-lg p-6 flex flex-col overflow-hidden"
          style={{ maxHeight: '85vh' }}
        >
          {body}
        </div>
      </div>
    </div>
  );
};

export default Paymentwithdraw;